/* ==========================================================================
   OneCircle – Sticky-Stacking für die Startseite (v3.1, Launch-Härtung)
   ==========================================================================
   Markiert die direkten Section-Wrapper in #MainContent als Stack-Ebenen:
   - ALLE Ebenen bekommen z-index in DOM-Reihenfolge + soliden Hintergrund
     (per CSS-Variable), damit spätere Sektionen frühere sauber abdecken.
   - Jede nicht ausgeschlossene Ebene wird sticky. Der Klebe-Punkt wird
     PRO SEKTION berechnet (CSS-Variable --oc-self-top): kurze Sektionen
     kleben am Offset (Kartenstapel), hohe scrollen erst komplett durch
     und kleben dann mit der Unterkante am unteren Viewport-Rand.
   - v3.1: Sektionen behalten ihre NATÜRLICHE Höhe. Die Viewport-Deckung
     übernimmt ein Backdrop-Pseudo-Element im Fix-CSS; dieses Script liest
     dafür die effektive Hintergrundfarbe des Section-Roots aus und setzt
     sie als CSS-Variable --oc-stack-backdrop auf den Wrapper.
   - Lesepuffer: Der Wert des Schema-Settings "oc_stack_hold" (Marker
     <div data-oc-stack-hold="…">, in % der Viewporthöhe) wird als
     Pixelwert in die CSS-Variable --oc-stack-hold auf dem Wrapper
     geschrieben; das Fix-CSS nutzt ihn als margin-bottom (Haltestrecke).
     Ab 990px wird der Wert auf 62,5% skaliert, weil Desktop schneller
     durchscrollt.
   - OneCircle Runde 8: Sektionen OHNE Marker bekommen 0 statt vormals 40.
     Nur Sektionen, die eine Haltestrecke ausdruecklich anfordern (also
     den Marker tragen), bekommen auch eine. Siehe Kommentar an der
     Fundstelle weiter unten.
   - Viewport-Messung über eine 100svh-Sonde statt window.innerHeight:
     stabil gegenüber der iOS-URL-Leiste (innerHeight ändert sich beim
     Scrollen, svh nicht).
   - resize wird NICHT mehr für jede Höhenänderung ausgewertet (iOS feuert
     resize beim Ein-/Ausblenden der URL-Leiste -> Klebepunkte sprangen
     mitten im Scrollen). Neu gerechnet wird nur bei Breitenänderung,
     orientationchange und nach window.load (Bilder ändern Sektionshöhen).
   - Mobil-Schalter und "Reduzierte Bewegung respektieren" des Controllers
     werden hier ausgewertet (data-Attribute): Sticky-Klassen werden dann
     gar nicht erst vergeben – Ebenen/z-index bleiben, damit weiterhin
     sauber abgedeckt wird, und Mindesthöhe/Puffer aus dem Fix-CSS
     entfallen automatisch.
   - Eine sticky Such-Bar (Marker [data-oc-sticky-search]) wird erkannt:
     ihre Höhe wird auf den Stack-Offset addiert; die Suche selbst ist
     KEINE Stack-Ebene.
   Läuft nur auf Seiten mit dem Controller (Startseite).
   ========================================================================== */
(function () {
  'use strict';

  var debounceTimer = null;
  var lastWidth = window.innerWidth;

  /* Deckende Farbe? transparent/alpha < 1 zaehlt nicht – dann soll das
     Backdrop auf den Controller-Fallback zurueckfallen statt halb-
     durchsichtig zu sein */
  function isOpaqueColor(color) {
    if (!color || color === 'transparent') return false;
    var m = color.match(/rgba?\(([^)]+)\)/);
    if (!m) return true; /* Hex/Farbname aus aelteren Engines */
    var parts = m[1].split(',');
    if (parts.length < 4) return true; /* rgb() ohne Alpha = deckend */
    return parseFloat(parts[3]) >= 0.99;
  }

  /* Stabile "kleine" Viewporthöhe (100svh) messen. Fallback: innerHeight
     (ältere Browser ohne svh -> Sonde bleibt 0 hoch). */
  function measureViewportHeight() {
    var probe = document.createElement('div');
    probe.style.cssText = 'position:fixed;top:0;left:0;width:0;height:100svh;visibility:hidden;pointer-events:none;';
    document.documentElement.appendChild(probe);
    var h = probe.offsetHeight;
    probe.parentNode.removeChild(probe);
    return h > 0 ? h : window.innerHeight;
  }

  function init() {
    var ctrl = document.querySelector('[data-oc-stacking]');
    var main = document.getElementById('MainContent');
    if (!main) return;

    var sections = Array.prototype.slice.call(main.children).filter(function (el) {
      return el.classList && el.classList.contains('shopify-section');
    });

    /* Aufräumen (wichtig für Editor-Reloads und Resize-Neuberechnung) */
    main.classList.remove('oc-stack-active');
    sections.forEach(function (el) {
      el.classList.remove('oc-stack__layer', 'oc-stack__sticky');
      el.style.removeProperty('z-index');
      el.style.removeProperty('--oc-self-top');
      el.style.removeProperty('--oc-stack-hold');
      el.style.removeProperty('--oc-stack-backdrop');
    });

    if (!ctrl || ctrl.getAttribute('data-enabled') !== 'true') return;

    /* Mobil-Schalter & reduzierte Bewegung: kein Sticky, aber weiterhin
       Ebenen (z-index + Hintergrund) für sauberes Abdecken */
    var isMobile = window.matchMedia('(max-width: 749px)').matches;
    var mobileEnabled = ctrl.getAttribute('data-mobile-enabled') !== 'false';
    var respectRm = ctrl.getAttribute('data-respect-rm') !== 'false';
    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var stickyAllowed = !(isMobile && !mobileEnabled) && !(respectRm && reducedMotion);

    var exclude = (ctrl.getAttribute('data-exclude') || '')
      .split(',')
      .map(function (s) { return s.trim().toLowerCase(); })
      .filter(Boolean);

    /* Basis-Offset aus der CSS-Variable (Desktop/Mobil via Media Query im
       Controller-CSS) – wird nie per Inline-Style überschrieben */
    var baseOffset = parseInt(getComputedStyle(main).getPropertyValue('--oc-stack-top-base'), 10);
    if (isNaN(baseOffset)) baseOffset = 80;

    /* Sticky Such-Bar: Höhe auf den Offset addieren, damit klebende
       Sektionen unter der Suche einrasten statt darunter zu rutschen */
    var offset = baseOffset;
    var searchMarker = document.querySelector('[data-oc-sticky-search]');
    var searchWrap = searchMarker ? searchMarker.closest('.shopify-section') : null;
    if (searchWrap) {
      offset += Math.ceil(searchWrap.getBoundingClientRect().height);
    }

    var vh = measureViewportHeight();
    var isDesktop = window.innerWidth >= 990;
    var layerIndex = 0;

    sections.forEach(function (el) {
      /* Controller und sticky Such-Bar sind keine Stack-Ebenen */
      if (el.querySelector('[data-oc-stacking]')) return;
      if (el === searchWrap) return;

      /* Jede Ebene: Hintergrund + aufsteigender z-index (spätere decken
         frühere ab) – auch ausgeschlossene Sektionen, sonst scheint eine
         klebende Sektion durch sie hindurch */
      layerIndex += 1;
      el.classList.add('oc-stack__layer');
      el.style.zIndex = String(layerIndex);

      /* Mobil deaktiviert / reduzierte Bewegung: nur Ebene, kein Sticky */
      if (!stickyAllowed) return;

      /* Per-Section-Toggle "Am Stacking-Effekt teilnehmen" (aus):
         Marker [data-oc-stack-skip] -> Sektion scrollt normal mit
         (kein Sticky), deckt aber weiterhin sauber ab */
      if (el.querySelector('[data-oc-stack-skip]')) return;

      /* Ausschluss-Liste: Begriff im Wrapper-Namen (Section-ID) */
      var id = (el.id || '').toLowerCase();
      for (var i = 0; i < exclude.length; i++) {
        if (id.indexOf(exclude[i]) !== -1) return;
      }

      /* Lesepuffer (Haltestrecke): Setting-Wert in % der Viewporthöhe.
         Desktop skaliert auf 62,5% (40% -> 25%), weil dort schneller
         gescrollt wird. Als Pixelwert gesetzt: unabhängig von
         svh-Unterstützung und iOS-URL-Leiste.

         OneCircle Runde 8: Default für markerlose Sektionen von 40 auf 0
         geändert. Vorher bekam JEDE Sektion ohne [data-oc-stack-hold] –
         also jede Theme-Sektion, die nicht aus der OC-Reihe stammt –
         ungefragt eine Haltestrecke von 40% (mobil) bzw. 25% (Desktop)
         der Viewporthöhe. Sichtbar wurde das am Video-Banner "Für eine
         bessere Welt": position:sticky endet an der Unterkante des
         Wrappers, die margin-bottom-Strecke gehört nicht mehr dazu, und
         das ::after-Element aus oc-stacking-fix.css malte sie in der
         Hintergrundfarbe der Sektion aus (dort scheme3 = #202020) –
         die gemeldete schwarze Fläche. Mit 0 bekommt nur noch eine
         Haltestrecke, wer sie über den Marker ausdrücklich anfordert;
         die elf OC-Sektionen tragen ihn und sind unverändert. */
      var holdMarker = el.querySelector('[data-oc-stack-hold]');
      var hold = holdMarker ? parseInt(holdMarker.getAttribute('data-oc-stack-hold'), 10) : NaN;
      if (isNaN(hold) || hold < 0) hold = 0;
      if (isDesktop) hold = hold * 0.625;
      el.style.setProperty('--oc-stack-hold', Math.round(vh * hold / 100) + 'px');

      /* Backdrop-Farbe (fuer ::before/::after im Fix-CSS): effektive
         Hintergrundfarbe des sichtbaren Section-Roots per Computed Style –
         respektiert Farbschema und Section-Settings, keine feste Farbe.
         Nur deckende Farben verwenden; bei transparentem Root bleibt die
         Variable ungesetzt und das CSS faellt auf var(--oc-stack-bg)
         (Controller-Setting "Fallback-Hintergrund der Ebenen") zurueck. */
      var rootEl = null;
      for (var c = 0; c < el.children.length; c++) {
        var kid = el.children[c];
        var tag = kid.tagName;
        if (kid.hasAttribute('hidden') || tag === 'LINK' || tag === 'STYLE' || tag === 'SCRIPT' || tag === 'NOSCRIPT') continue;
        rootEl = kid;
        break;
      }
      if (rootEl) {
        var bg = getComputedStyle(rootEl).backgroundColor;
        if (isOpaqueColor(bg)) {
          el.style.setProperty('--oc-stack-backdrop', bg);
        }
      }

      /* Klebe-Punkt pro Sektion (natürliche Höhe, seit v3.1 ohne
         Mindesthöhen-Einfluss):
         kurz  -> am Offset kleben (Kartenstapel)
         hoch  -> erst komplett durchscrollen, dann mit der Unterkante
                  am Viewport-Ende kleben (top wird negativ) */
      el.classList.add('oc-stack__sticky');
      var h = el.offsetHeight;
      var top = (h + offset <= vh) ? offset : (vh - h);
      el.style.setProperty('--oc-self-top', top + 'px');
    });

    main.classList.add('oc-stack-active');
  }

  function schedule() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(init, 200);
  }

  /* iOS feuert resize beim Ein-/Ausblenden der URL-Leiste – nur bei
     echter Breitenänderung (Rotation, Split-Screen) neu rechnen */
  function onResize() {
    if (window.innerWidth === lastWidth) return;
    lastWidth = window.innerWidth;
    schedule();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  /* Nach vollständigem Laden (Bilder ändern Sektionshöhen) neu messen */
  window.addEventListener('load', schedule);
  window.addEventListener('resize', onResize, { passive: true });
  window.addEventListener('orientationchange', schedule, { passive: true });

  /* Theme-Editor: bei Section-Änderungen neu initialisieren */
  document.addEventListener('shopify:section:load', schedule);
  document.addEventListener('shopify:section:unload', schedule);
  document.addEventListener('shopify:section:reorder', schedule);
})();